import { BLOCK_TYPES, type BlockType } from "@aurumiq/cms-schema";
import { Button } from "@aurumiq/ui/components/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@aurumiq/ui/components/dropdown-menu";
import { StatusTag } from "@aurumiq/ui/composite/status-tag";
import { cn } from "@aurumiq/ui/lib/utils";
import { AlertTriangle, Copy, Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { SortableList } from "@/components/sortable-list";
import { BLOCK_LABELS, blockSummary, type EditorBlock, isBlockType, validateBlock } from "./blocks";

export interface BlockListProps {
  blocks: EditorBlock[];
  selectedKey: string | null;
  onSelect: (key: string) => void;
  onReorder: (next: EditorBlock[]) => void;
  onAdd: (type: BlockType) => void;
  onToggleHidden: (key: string) => void;
  onDuplicate: (key: string) => void;
  onDelete: (key: string) => void;
}

export function BlockList(p: BlockListProps) {
  return (
    <div className="grid gap-2">
      <div className="flex items-center justify-between">
        <span className="font-semibold text-sm">Blocks</span>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="outline" size="sm">
              <Plus /> Add block
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            {BLOCK_TYPES.map((t) => (
              <DropdownMenuItem key={t} onSelect={() => p.onAdd(t)}>
                {BLOCK_LABELS[t]}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {p.blocks.length === 0 ? (
        <p className="rounded-lg border border-dashed p-3 text-center text-muted-foreground text-xs">
          No blocks yet. Add one to start.
        </p>
      ) : null}
      <SortableList
        items={p.blocks}
        getId={(b) => b.key}
        onReorder={p.onReorder}
        ariaLabel="Blocks"
        rowClassName={(b) => {
          const v = validateBlock(b.raw);
          return cn(
            b.key === p.selectedKey && "border-primary ring-2 ring-ring/30",
            !v.ok && "border-danger bg-danger-bg",
          );
        }}
        renderItem={(b) => {
          const v = validateBlock(b.raw);
          const type = b.raw.type;
          const label = isBlockType(type) ? BLOCK_LABELS[type] : `Unknown “${String(type ?? "?")}”`;
          const hidden = b.raw.hidden === true;
          return (
            <div className="flex min-w-0 items-center gap-0.5 py-1 pr-1">
              <button
                type="button"
                className="min-w-0 flex-1 overflow-hidden py-1 text-left"
                onClick={() => p.onSelect(b.key)}
                aria-current={b.key === p.selectedKey ? "true" : undefined}
                data-invalid={v.ok ? undefined : "true"}
              >
                <div className="flex items-center gap-1.5 text-sm">
                  {!v.ok ? (
                    <AlertTriangle className="size-3.5 text-danger" aria-label="Invalid block" />
                  ) : null}
                  <span
                    className={cn(
                      "truncate font-medium",
                      hidden && "text-muted-foreground line-through",
                    )}
                  >
                    {label}
                  </span>
                  {!v.ok ? <StatusTag variant="err">INVALID</StatusTag> : null}
                </div>
                <div className="truncate text-muted-foreground text-xs">{blockSummary(b.raw)}</div>
              </button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label={hidden ? `Show ${label}` : `Hide ${label}`}
                onClick={() => p.onToggleHidden(b.key)}
              >
                {hidden ? <EyeOff /> : <Eye />}
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7"
                aria-label={`Duplicate ${label}`}
                onClick={() => p.onDuplicate(b.key)}
              >
                <Copy />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 text-destructive"
                aria-label={`Delete ${label}`}
                onClick={() => p.onDelete(b.key)}
              >
                <Trash2 />
              </Button>
            </div>
          );
        }}
      />
    </div>
  );
}
